import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { DataTable, type Column } from '@/components/DataTable'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { findNearbyLeads, type Lead } from '@/features/customers/api'
import { CustomerFormDialog } from '@/features/customers/CustomerFormDialog'

const RADII = [1000, 2000, 5000, 10000] as const

const PRIORITY_STYLE: Record<Lead['priority'], string> = {
  hot: 'bg-success/10 text-success',
  warm: 'bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-400',
  cold: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
}

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

interface Search {
  latitude: number
  longitude: number
  radius: number
}

/**
 * Shops near a point that are not customers yet, best first. Google Places finds them; the
 * server drops the ones already on the books and scores the rest on distance, rating,
 * review count and how much of the range the shop type carries.
 */
export function NearbyLeads() {
  const [latitude, setLatitude] = useState('')
  const [longitude, setLongitude] = useState('')
  const [radius, setRadius] = useState<number>(2000)
  const [search, setSearch] = useState<Search | null>(null)
  const [locating, setLocating] = useState(false)
  const [locateError, setLocateError] = useState<string | null>(null)
  const [signingUp, setSigningUp] = useState<Lead | null>(null)

  const leads = useQuery({
    queryKey: ['leads', 'nearby', search],
    queryFn: () => findNearbyLeads(search!.latitude, search!.longitude, search!.radius),
    enabled: search !== null,
    retry: false,
  })

  const lat = Number(latitude)
  const lng = Number(longitude)
  const valid = latitude !== '' && longitude !== '' && Math.abs(lat) <= 90 && Math.abs(lng) <= 180

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setLocateError('This browser cannot share a location.')
      return
    }
    setLocating(true)
    setLocateError(null)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude.toFixed(6))
        setLongitude(position.coords.longitude.toFixed(6))
        setLocating(false)
      },
      () => {
        setLocateError('Location was not shared. Type the coordinates instead.')
        setLocating(false)
      },
    )
  }

  const columns: Column<Lead>[] = [
    {
      key: 'name',
      header: 'Shop',
      className: 'font-medium',
      sortValue: (row) => row.name,
      cell: (row) => (
        <div>
          <div>{row.name}</div>
          {row.address && (
            <div className="text-xs font-normal text-slate-500 dark:text-slate-400">
              {row.address}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'score',
      header: 'Score',
      sortValue: (row) => row.score,
      cell: (row) => (
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_STYLE[row.priority]}`}
        >
          {row.priority} · {row.score}
        </span>
      ),
    },
    {
      key: 'distance',
      header: 'Distance',
      className: 'tabular-nums',
      sortValue: (row) => row.distance_m,
      cell: (row) => `${(row.distance_m / 1000).toFixed(1)} km`,
    },
    {
      key: 'why',
      header: 'Why',
      className: 'text-xs text-slate-500 dark:text-slate-400',
      cell: (row) => row.reasons.join('; '),
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <Button size="sm" variant="secondary" onClick={() => setSigningUp(row)}>
          Add as customer
        </Button>
      ),
    },
  ]

  const data = leads.data

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Nearby leads</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Shops around a point that are not customers yet, scored on distance, rating, reviews and
          shop type.
        </p>
      </header>

      <Card>
        <form
          className="flex flex-wrap items-end gap-3 p-5"
          onSubmit={(event) => {
            event.preventDefault()
            if (valid) setSearch({ latitude: lat, longitude: lng, radius })
          }}
        >
          <label className="text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">Latitude</span>
            <Input
              inputMode="decimal"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              placeholder="12.971600"
              className="w-36"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">Longitude</span>
            <Input
              inputMode="decimal"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              placeholder="77.594600"
              className="w-36"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">Within</span>
            <select
              className={SELECT_CLASS}
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
            >
              {RADII.map((r) => (
                <option key={r} value={r}>
                  {r / 1000} km
                </option>
              ))}
            </select>
          </label>
          <Button type="button" variant="secondary" loading={locating} onClick={useMyLocation}>
            Use my location
          </Button>
          <Button type="submit" disabled={!valid} loading={leads.isFetching}>
            Find leads
          </Button>
        </form>
        {locateError && (
          <div className="px-5 pb-4">
            <Alert tone="error">{locateError}</Alert>
          </div>
        )}
      </Card>

      {data?.unavailable_reason && (
        <Alert tone="info" title="Lead search is unavailable">
          {data.unavailable_reason}
        </Alert>
      )}
      {leads.error && <Alert tone="error">Could not search for leads.</Alert>}

      {data && !data.unavailable_reason && (
        <Card>
          <CardHeader
            title={`${data.leads.length} lead${data.leads.length === 1 ? '' : 's'}`}
            description={
              data.already_customers
                ? `${data.already_customers} nearby shop${data.already_customers === 1 ? ' is' : 's are'} already customers and left out.`
                : undefined
            }
          />
          <DataTable
            rows={data.leads}
            columns={columns}
            getRowId={(row) => row.place_id}
            caption="Nearby leads, best first"
            emptyMessage="No shops found here that are not already customers."
          />
        </Card>
      )}

      {signingUp && (
        <CustomerFormDialog
          open
          draft={{
            name: signingUp.name,
            phone: signingUp.phone ?? undefined,
            address_line1: signingUp.address ?? undefined,
            notes: `Lead from Google Places (score ${signingUp.score}) at ${signingUp.latitude.toFixed(6)}, ${signingUp.longitude.toFixed(6)}.`,
          }}
          onClose={() => setSigningUp(null)}
        />
      )}
    </div>
  )
}
